import {
  Modal,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Popover,
  List,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Button,
  Box,
  ListItem,
  ListItemText,
  useTheme,
} from '@mui/material';
import { useState, useRef, useMemo } from 'react';
import {
  GetInspectionItem,
  GetRepairRequest,
} from '../../../../../../services/apiTypes';
import {
  DIALOG_BOX_STYLE,
  HEADER_HEIGHT,
  TEXT_OVERFLOW_2LINES,
} from '../../../../../../_constants/styleConst';
import { SelectItemType } from './LeakTest';
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslation } from 'react-i18next';
import {
  WET_LEAK_TEST_SECTION,
  LEAK_TEST,
  MODEL_ATTRIBUTES,
} from '../../../../../../_constants/inspectionConst';
import { MaxLength } from '../../../../../../_constants/maxLengthConst';

type WetLeakTestModalProps = {
  isOpen: boolean;
  wetLeakTestItems: GetInspectionItem;
  handleClose: (
    shouldUpdate: boolean,
    selectedItemsModal: SelectItemType[],
  ) => void;
  repairRequest: GetRepairRequest | undefined;
};

const style = {
  section: {
    position: 'absolute',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    boxSizing: 'border-box',
    cursor: 'pointer',
    backgroundColor: 'transparent',
  },
  cell: {
    fontWeight: 'bold',
    padding: '12px',
  },
};

/** 水没気密検査の画像選択モーダル */
const WetLeakTestModal: React.FC<WetLeakTestModalProps> = ({
  isOpen,
  wetLeakTestItems,
  handleClose,
  repairRequest,
}) => {
  const { t } = useTranslation();
  const imageRef = useRef(null);
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  /** 選択したセクションID */
  const [sectionId, setSectionId] = useState<string>('');
  /** 選択したセクションのデータ */
  const [sectionData, setSectionData] = useState<GetInspectionItem>();
  /** 選択したセクションのデータ(OSA2専用) */
  const [sectionData2, setSectionData2] = useState<GetInspectionItem>();

  /** モーダルで選択中の問題箇所のリスト */
  const [selectedItemsModal, setSelectedItemsModal] = useState<
    SelectItemType[]
  >([]);

  /** セクションをクリック */
  const handleCellClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    id: number,
  ) => {
    const typeId = `${LEAK_TEST.typeId}-${id}`;
    setSectionId(typeId);
    setSectionData(
      wetLeakTestItems.itemData!.find((item) => item.id === typeId),
    );
    setAnchorEl(event.currentTarget);
    if (typeId === `${LEAK_TEST.typeId}-${WET_LEAK_TEST_SECTION.OSA1}`) {
      // OSA1の場合はOSA2のデータもセットする
      setSectionData2(
        wetLeakTestItems.itemData!.find(
          (item) =>
            item.id === `${LEAK_TEST.typeId}-${WET_LEAK_TEST_SECTION.OSA2}`,
        ),
      );
    } else {
      setSectionData2(undefined);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  /** ラベルをクリック */
  const onClickLabel = (detail: GetInspectionItem) => {
    setSelectedItemsModal((prev) => {
      const newItems = [...prev];

      // OSA2の場合はsectionIdの読み替えを行う
      const setSectionId =
        sectionId === `${LEAK_TEST.typeId}-${WET_LEAK_TEST_SECTION.OSA1}` &&
        WET_LEAK_TEST_SECTION.OSA2 <
          Number(detail.id.slice(detail.id.lastIndexOf('-') + 1))
          ? `${LEAK_TEST.typeId}-${WET_LEAK_TEST_SECTION.OSA2}`
          : sectionId;

      const hasNewItem = newItems.some(
        (item) =>
          item.sectionId === setSectionId && item.detailId === detail.id,
      );
      if (!hasNewItem && sectionData) {
        newItems.push({
          sectionId: setSectionId,
          sectionLabel:
            setSectionId === `${LEAK_TEST.typeId}-${WET_LEAK_TEST_SECTION.OSA2}`
              ? sectionData2
                ? sectionData2.label
                : sectionData.label
              : sectionData.label, // OSA2の場合はlabelの読み替えを行う
          detailId: detail.id,
          detailLabel: detail.label,
          memo: '',
        });
      }
      return newItems;
    });
  };

  /** メモを入力 */
  const onChangeMemo = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    changeItem: SelectItemType,
  ) => {
    setSelectedItemsModal((prev) =>
      prev.map((item) =>
        item.sectionId === changeItem.sectionId &&
        item.detailId === changeItem.detailId
          ? { ...item, memo: event.target.value }
          : item,
      ),
    );
  };

  /** アイテムを削除 */
  const handleDelete = (deleteItem: SelectItemType) => {
    setSelectedItemsModal((prev) =>
      prev.filter(
        (item) =>
          item.sectionId !== deleteItem.sectionId ||
          item.detailId !== deleteItem.detailId,
      ),
    );
  };

  /** 表示する画像の種類を判定する */
  const getImageType = (imageTypes: { [key: string]: number }) => {
    if (!repairRequest || !repairRequest.modelInfo) {
      return imageTypes.leakTest1;
    }
    const modelInfo = [
      repairRequest.modelInfo.attributeId1,
      repairRequest.modelInfo.attributeId2,
      repairRequest.modelInfo.attributeId3,
      repairRequest.modelInfo.attributeId5,
      repairRequest.modelInfo.attributeId6,
    ];
    // 画像の種類の判定 優先順位5>3>4>2>1
    if (modelInfo.includes(MODEL_ATTRIBUTES.G7_SLIM)) {
      return imageTypes.leakTest5;
    }
    if (
      modelInfo.includes(MODEL_ATTRIBUTES.EUS) ||
      modelInfo.includes(MODEL_ATTRIBUTES.EBUS)
    ) {
      if (modelInfo.includes(MODEL_ATTRIBUTES.TWO_WAY)) {
        return imageTypes.leakTest3;
      }
    }
    if (modelInfo.includes(MODEL_ATTRIBUTES.SLIM)) {
      return imageTypes.leakTest4;
    }
    if (modelInfo.includes(MODEL_ATTRIBUTES.TWO_WAY)) {
      return imageTypes.leakTest2;
    }
    return imageTypes.leakTest1;
  };

  /** 画像の切り替え */
  const setImageStyle = () => {
    // デフォルトの画像とstyle設定
    const style = {
      src: 'images/Wet_Leak_1.png',
      FSB: {
        top: '0%',
        left: '0%',
        width: '50%',
        height: '50%',
      },
      Connector: {
        top: '50%',
        left: '0%',
        width: '50%',
        height: '30%',
      },
      OSA: {
        top: '0%',
        left: '50%',
        width: '50%',
        height: '15%',
      },
      OSA3: {
        top: '15%',
        left: '50%',
        width: '50%',
        height: '25%',
      },
      FSA: {
        top: '40%',
        left: '50%',
        width: '50%',
        height: '55%',
      },
      DTA: {
        top: '95%',
        left: '50%',
        width: '50%',
        height: '5%',
      },
    };
    // 画像の種類
    const imageTypes = {
      leakTest1: 1,
      leakTest2: 2,
      leakTest3: 3,
      leakTest4: 4,
      leakTest5: 5,
    };
    // 判定した種類で画像切り替え
    const imageType = getImageType(imageTypes);
    switch (imageType) {
      case imageTypes.leakTest5:
        style.src = 'images/Wet_Leak_5.png';
        // 左側
        style.FSB.top = '0%';
        style.FSB.left = '0%';
        style.FSB.width = '50%';
        style.FSB.height = '42%';
        style.Connector.top = '42%';
        style.Connector.left = '0%';
        style.Connector.width = '50%';
        style.Connector.height = '40%';
        // 右側
        style.OSA.top = '0%';
        style.OSA.left = '50%';
        style.OSA.width = '50%';
        style.OSA.height = '19%';
        style.OSA3.top = '19%';
        style.OSA3.left = '50%';
        style.OSA3.width = '50%';
        style.OSA3.height = '36%';
        style.FSA.top = '55%';
        style.FSA.left = '50%';
        style.FSA.width = '50%';
        style.FSA.height = '38%';
        style.DTA.top = '93%';
        style.DTA.left = '50%';
        style.DTA.width = '50%';
        style.DTA.height = '7%';
        break;
      case imageTypes.leakTest4:
        style.src = 'images/Wet_Leak_4.png';
        // 左側
        style.FSB.top = '0%';
        style.FSB.left = '0%';
        style.FSB.width = '50%';
        style.FSB.height = '68%';
        style.Connector.top = '68%';
        style.Connector.left = '0%';
        style.Connector.width = '50%';
        style.Connector.height = '32%';
        // 右側
        style.OSA.top = '0%';
        style.OSA.left = '50%';
        style.OSA.width = '50%';
        style.OSA.height = '21%';
        style.OSA3.top = '21%';
        style.OSA3.left = '50%';
        style.OSA3.width = '50%';
        style.OSA3.height = '19%';
        style.FSA.top = '40%';
        style.FSA.left = '50%';
        style.FSA.width = '50%';
        style.FSA.height = '50%';
        style.DTA.top = '90%';
        style.DTA.left = '50%';
        style.DTA.width = '50%';
        style.DTA.height = '10%';
        break;
      case imageTypes.leakTest3:
        style.src = 'images/Wet_Leak_3.png';
        // 左側
        style.FSB.top = '0%';
        style.FSB.left = '0%';
        style.FSB.width = '65%';
        style.FSB.height = '60%';
        style.Connector.top = '60%';
        style.Connector.left = '0%';
        style.Connector.width = '65%';
        style.Connector.height = '40%';
        // 右側
        style.OSA.top = '0%';
        style.OSA.left = '65%';
        style.OSA.width = '35%';
        style.OSA.height = '20%';
        style.OSA3.top = '20%';
        style.OSA3.left = '65%';
        style.OSA3.width = '35%';
        style.OSA3.height = '18%';
        style.FSA.top = '38%';
        style.FSA.left = '65%';
        style.FSA.width = '35%';
        style.FSA.height = '52%';
        style.DTA.top = '90%';
        style.DTA.left = '65%';
        style.DTA.width = '35%';
        style.DTA.height = '10%';
        break;
      case imageTypes.leakTest2:
        style.src = 'images/Wet_Leak_2.png';
        // 左側
        style.FSB.top = '0%';
        style.FSB.left = '0%';
        style.FSB.width = '50%';
        style.FSB.height = '60%';
        style.Connector.top = '60%';
        style.Connector.left = '0%';
        style.Connector.width = '50%';
        style.Connector.height = '40%';
        // 右側
        style.OSA.top = '0%';
        style.OSA.left = '50%';
        style.OSA.width = '50%';
        style.OSA.height = '21%';
        style.OSA3.top = '21%';
        style.OSA3.left = '50%';
        style.OSA3.width = '50%';
        style.OSA3.height = '16%';
        style.FSA.top = '37%';
        style.FSA.left = '50%';
        style.FSA.width = '50%';
        style.FSA.height = '54%';
        style.DTA.top = '91%';
        style.DTA.left = '50%';
        style.DTA.width = '50%';
        style.DTA.height = '9%';
        break;
      default:
        // 1またはいずれも当てはまらないものはデフォルトのスタイルを適用
        break;
    }
    return style;
  };
  const imageStyle = useMemo(() => setImageStyle(), []);

  /** モーダルを閉じる */
  const closeModal = (
    shouldUpdate: boolean,
    selectedItemsModal: SelectItemType[],
  ) => {
    handleClose(shouldUpdate, selectedItemsModal);
    setSelectedItemsModal([]);
  };

  return (
    <Modal
      open={isOpen}
      onClose={() => closeModal(false, selectedItemsModal)}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box
        sx={{
          ...DIALOG_BOX_STYLE,
          width: '80%',
          maxWidth: 'none',
          height: '95%',
          bgcolor: 'background.default',
          p: undefined,
        }}
      >
        <Box>
          <CssBaseline />
          <AppBar position="fixed" sx={{ bgcolor: 'primary.main' }}>
            <Toolbar>
              <Typography
                variant="h6"
                noWrap
                height={'40px'}
                pt={'5px'}
                sx={TEXT_OVERFLOW_2LINES}
              >
                {wetLeakTestItems.label}
              </Typography>
            </Toolbar>
          </AppBar>
          <Box
            sx={{
              display: 'flex',
              marginTop: HEADER_HEIGHT,
            }}
          >
            {/* 画像領域 */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {/* 全体画像 */}
              <Box
                sx={{
                  position: 'relative',
                  width: 'fit-content',
                  height: '75dvh',
                  marginTop: '20px',
                }}
              >
                <img
                  ref={imageRef}
                  src={imageStyle.src}
                  alt="background"
                  width="100%"
                  height="100%"
                />
                <Box
                  key={'FSB'}
                  onClick={(event) =>
                    handleCellClick(event, WET_LEAK_TEST_SECTION.FSB)
                  }
                  sx={{
                    ...style.section,
                    top: imageStyle.FSB.top,
                    left: imageStyle.FSB.left,
                    width: imageStyle.FSB.width,
                    height: imageStyle.FSB.height,
                  }}
                />
                <Box
                  key={'Connector'}
                  onClick={(event) =>
                    handleCellClick(event, WET_LEAK_TEST_SECTION.Connector)
                  }
                  sx={{
                    ...style.section,
                    top: imageStyle.Connector.top,
                    left: imageStyle.Connector.left,
                    width: imageStyle.Connector.width,
                    height: imageStyle.Connector.height,
                  }}
                />

                <Box
                  key={'OSA'}
                  onClick={
                    (event) =>
                      handleCellClick(
                        event,
                        WET_LEAK_TEST_SECTION.OSA1,
                      ) /* OSA1,2があるがOSA1を入れる */
                  }
                  sx={{
                    ...style.section,
                    top: imageStyle.OSA.top,
                    left: imageStyle.OSA.left,
                    width: imageStyle.OSA.width,
                    height: imageStyle.OSA.height,
                  }}
                />
                <Box
                  key={'OSA3'}
                  onClick={(event) =>
                    handleCellClick(event, WET_LEAK_TEST_SECTION.OSA3)
                  }
                  sx={{
                    ...style.section,
                    top: imageStyle.OSA3.top,
                    left: imageStyle.OSA3.left,
                    width: imageStyle.OSA3.width,
                    height: imageStyle.OSA3.height,
                  }}
                />
                <Box
                  key={'FSA'}
                  onClick={(event) =>
                    handleCellClick(event, WET_LEAK_TEST_SECTION.FSA)
                  }
                  sx={{
                    ...style.section,
                    top: imageStyle.FSA.top,
                    left: imageStyle.FSA.left,
                    width: imageStyle.FSA.width,
                    height: imageStyle.FSA.height,
                  }}
                />
                <Box
                  key={'DTA'}
                  onClick={(event) =>
                    handleCellClick(event, WET_LEAK_TEST_SECTION.DTA)
                  }
                  sx={{
                    ...style.section,
                    top: imageStyle.DTA.top,
                    left: imageStyle.DTA.left,
                    width: imageStyle.DTA.width,
                    height: imageStyle.DTA.height,
                  }}
                />
              </Box>

              {/* ラベルの選択肢 */}
              <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                sx={{ maxHeight: '75dvh' }}
              >
                <List sx={{ bgcolor: 'background.default' }}>
                  {sectionData?.itemData?.map((detail, i) => (
                    <ListItem
                      key={`wet_leak_item_${i}`}
                      sx={{
                        cursor: 'pointer',
                        ':hover': {
                          bgcolor: 'background.paper',
                        },
                      }}
                      onClick={() => onClickLabel(detail)}
                    >
                      <ListItemText primary={detail.label} />
                    </ListItem>
                  ))}
                  {/* OSA2のリスト表示専用 */}
                  {sectionData2?.itemData?.map((detail, i) => (
                    <ListItem
                      key={`wet_leak_osa2_${i}`}
                      sx={{
                        cursor: 'pointer',
                        ':hover': {
                          bgcolor: 'background.paper',
                        },
                      }}
                      onClick={() => onClickLabel(detail)}
                    >
                      <ListItemText primary={detail.label} />
                    </ListItem>
                  ))}
                </List>
              </Popover>
            </Box>

            {/* 気密不良箇所 */}
            <Box
              sx={{
                flex: 1,
                marginTop: '1rem',
                padding: '1rem',
                maxHeight: 'calc(70dvh + 2rem)',
                bgcolor: 'background.paper',
              }}
            >
              <TableContainer
                component={Paper}
                sx={{
                  bgcolor: 'background.default',
                  maxHeight: '70dvh',
                  width: '45vw',
                  overflow: 'auto',
                  boxShadow: 'none',
                }}
              >
                <Table stickyHeader={true}>
                  <TableHead>
                    <TableRow sx={{ fontWeight: 'bold' }}>
                      <TableCell width={'20%'} sx={style.cell}></TableCell>
                      <TableCell width={'30%'} sx={style.cell}>
                        {t('LBL_LEAKAGE_POINT')}
                      </TableCell>
                      <TableCell width={'45%'} sx={style.cell}>
                        {t('LBL_MEMO')}
                      </TableCell>
                      <TableCell width={'5%'} sx={style.cell}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedItemsModal.map((item, i) => (
                      <TableRow key={`wet_leak_selected_${i}`} sx={style.cell}>
                        <TableCell size="small">{item.sectionLabel}</TableCell>
                        <TableCell size="small">
                          <Typography sx={TEXT_OVERFLOW_2LINES}>
                            {item.detailLabel}
                          </Typography>
                        </TableCell>
                        <TableCell size="small">
                          <TextField
                            id={`leak_${i}`}
                            value={item.memo}
                            fullWidth
                            size="small"
                            slotProps={{
                              htmlInput: {
                                maxLength: MaxLength.WetLeakTest.MEMO,
                              },
                            }}
                            onChange={(event) => onChangeMemo(event, item)}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            sx={{ minWidth: '24px', padding: '0' }}
                            onClick={() => handleDelete(item)}
                          >
                            <ClearIcon
                              sx={{
                                fontSize: '18px',
                                color: theme.palette.secondary.main,
                              }}
                            />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </Box>
        <Button
          onClick={() => closeModal(false, selectedItemsModal)}
          variant="contained"
          color="inherit"
          sx={{
            position: 'absolute',
            right: '14%',
            bottom: '2%',
            width: '10%',
          }}
        >
          {t('BTN_CANCEL')}
        </Button>

        <Button
          onClick={() => {
            closeModal(true, selectedItemsModal);
          }}
          variant="contained"
          sx={{ position: 'absolute', right: '2%', bottom: '2%', width: '10%' }}
          disabled={selectedItemsModal.length === 0}
        >
          {t('BTN_OK')}
        </Button>
      </Box>
    </Modal>
  );
};

export default WetLeakTestModal;
